import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { OpBooking, OpGuestOp, RoomStatus } from './RoomOperationBoard';
import { isImportedInHouseBooking, isCurrentlyInHouse } from '@/pages/dashboard/utils/bookingOperations';
import NoShowModal from '@/pages/reservations/components/NoShowModal';

interface Props {
  open: boolean;
  booking: OpBooking | null;
  roomName: string;
  guestOps: OpGuestOp | null;
  roomStatus: RoomStatus;
  checkinBlockedReason: string | null;
  todayJst: string;
  onClose: () => void;
  onCheckIn: (bookingId: string) => Promise<void>;
  onCheckOut: (bookingId: string) => Promise<void>;
  onImportedCheckOut: (bookingId: string) => Promise<void>;
  onNoShow: (bookingId: string, reason: string) => Promise<void>;
  actionLoading: boolean;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return dateStr;
}

function formatTimeOnly(timeStr: string | null): string {
  if (!timeStr) return '—';
  if (/^\d{2}:\d{2}/.test(timeStr)) return timeStr.slice(0, 5);
  return '—';
}

function formatTimestamptzJST(isoStr: string | null): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

const C = {
  bg: '#FFFEFA',
  text: '#252824',
  muted: '#6B6B67',
  primary: '#234638',
  accent: '#A8843A',
  border: '#DED9CC',
};

export default function BookingDetailDrawer({ open, booking, roomName, guestOps, roomStatus, checkinBlockedReason, todayJst, onClose, onCheckIn, onCheckOut, onImportedCheckOut, onNoShow, actionLoading }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [confirmAction, setConfirmAction] = useState<'check_in' | 'check_out' | 'imported_check_out' | null>(null);
  const [operatorNames, setOperatorNames] = useState<{ checkedInBy: string | null; checkedOutBy: string | null }>({ checkedInBy: null, checkedOutBy: null });
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [noShowModalOpen, setNoShowModalOpen] = useState(false);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Fetch operator names when booking changes
  useEffect(() => {
    if (!booking) {
      setOperatorNames({ checkedInBy: null, checkedOutBy: null });
      return;
    }
    const ids: string[] = [];
    if (booking.checked_in_by) ids.push(booking.checked_in_by);
    if (booking.checked_out_by) ids.push(booking.checked_out_by);
    if (ids.length === 0) {
      setOperatorNames({ checkedInBy: null, checkedOutBy: null });
      return;
    }

    let cancelled = false;
    setOperatorsLoading(true);
    supabase.from('profiles').select('id, full_name').in('id', ids).then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        // RLS restriction or other error — fallback to "記録済み"
        setOperatorNames({
          checkedInBy: booking.checked_in_by ? '記録済み' : null,
          checkedOutBy: booking.checked_out_by ? '記録済み' : null,
        });
      } else {
        const map = new Map<string, string>();
        data.forEach((p) => map.set(p.id, p.full_name || '記録済み'));
        setOperatorNames({
          checkedInBy: booking.checked_in_by ? (map.get(booking.checked_in_by) || '記録済み') : null,
          checkedOutBy: booking.checked_out_by ? (map.get(booking.checked_out_by) || '記録済み') : null,
        });
      }
      setOperatorsLoading(false);
    });
    return () => { cancelled = true; };
  }, [booking?.id, booking?.checked_in_by, booking?.checked_out_by]);

  // Reset confirmation on close
  useEffect(() => {
    if (!open) setConfirmAction(null);
  }, [open]);

  if (!booking) return null;

  const channelDisplay = booking.source_override || booking.booking_channel || '—';

  const hasGuestOps = !!(guestOps && (
    guestOps.early_checkin_requested ||
    guestOps.early_checkin_approved ||
    guestOps.late_checkout_requested ||
    guestOps.late_checkout_approved
  ));

  // Check-in / check-out button conditions
  const isCancelled = booking.booking_status === 'cancelled' || booking.operational_status === 'cancelled';
  const importedInHouse = isImportedInHouseBooking(booking, todayJst);
  const currentlyInHouse = isCurrentlyInHouse(booking);
  const isNoShow = booking.operational_status === 'no_show';
  // 通常の当日到着予約（expected + arrival_date === todayJst）かつ部屋が準備完了 → チェックインボタン
  const bookingEligible = !isCancelled && !importedInHouse && booking.operational_status === 'expected' && booking.arrival_date === todayJst;
  const canCheckIn = bookingEligible && checkinBlockedReason === null && !isNoShow;
  // 移行時在室予約 → チェックアウトボタン（特殊フロー）
  const canImportedCheckOut = !isCancelled && importedInHouse;
  // 通常の在室予約 → チェックアウトボタン
  const canCheckOut = !isCancelled && currentlyInHouse && !importedInHouse;
  // ノーショー記録ボタン: expected + 到着日が今日以前 + 未チェックイン + キャンセルなし
  const canNoShow = !isCancelled && !isNoShow && booking.operational_status === 'expected' && booking.actual_check_in_at == null && booking.arrival_date <= todayJst;

  const handleConfirmCheckIn = async () => {
    setConfirmAction(null);
    await onCheckIn(booking.id);
  };

  const handleConfirmCheckOut = async () => {
    setConfirmAction(null);
    await onCheckOut(booking.id);
  };

  const handleConfirmImportedCheckOut = async () => {
    setConfirmAction(null);
    await onImportedCheckOut(booking.id);
  };

  const handleNoShowSubmit = async (reason: string) => {
    setNoShowModalOpen(false);
    await onNoShow(booking.id, reason);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        onClick={onClose}
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}
        aria-hidden="true"
      />

      {/* Drawer panel — desktop: right slide, mobile: bottom slide */}
      <div
        className={`fixed z-50 flex flex-col overflow-y-auto transition-transform duration-200
          inset-y-0 right-0 w-full sm:max-w-md
          max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:max-h-[85vh] max-sm:rounded-t-xl
          ${open ? 'translate-x-0 max-sm:translate-y-0' : 'translate-x-full max-sm:translate-y-full'}
        `}
        style={{ backgroundColor: C.bg, boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.12)' : 'none' }}
        role="dialog"
        aria-modal="true"
        aria-label={`${booking.guest_display_name || 'ゲスト'} 予約詳細`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: C.text }}>予約詳細</h3>
            <p className="text-xs mt-0.5" style={{ color: C.muted }}>{roomName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors hover:bg-background-100"
            aria-label="閉じる"
            style={{ color: C.muted }}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 py-4 space-y-4">
          {/* Room status */}
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide" style={{ color: C.muted }}>部屋状態</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${
              roomStatus === 'occupied'
                ? ''
                : roomStatus === 'checked_out'
                  ? ''
                  : ''
            }`}
              style={
                roomStatus === 'occupied'
                  ? { backgroundColor: C.primary, color: '#FFFEFA' }
                  : roomStatus === 'checked_out'
                    ? { backgroundColor: C.accent, color: '#FFFEFA' }
                    : { backgroundColor: 'transparent', color: C.primary, border: `1px solid ${C.primary}` }
              }
            >
              {roomStatus === 'occupied' && <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-line text-xs"></i></span>}
              {roomStatus === 'checked_out' && <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-logout-box-line text-xs"></i></span>}
              {roomStatus === 'ready' && <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-check-line text-xs"></i></span>}
              {roomStatus === 'occupied' ? '在室' : roomStatus === 'checked_out' ? 'チェックアウト済' : '準備完了'}
            </span>
          </div>

          {/* Guest name */}
          <div>
            <p className="text-xs uppercase tracking-wide mb-1" style={{ color: C.muted }}>ゲスト</p>
            <p className="text-lg font-bold" style={{ color: C.text }}>{booking.guest_display_name || 'ゲスト'}</p>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md p-3" style={{ backgroundColor: '#F6F4EE' }}>
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>到着</p>
              <p className="text-sm font-semibold" style={{ color: C.text }}>{formatDate(booking.arrival_date)}</p>
              <p className="text-xs mt-0.5" style={{ color: C.muted }}>{formatTimeOnly(booking.arrival_time)}</p>
            </div>
            <div className="rounded-md p-3" style={{ backgroundColor: '#F6F4EE' }}>
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>出発</p>
              <p className="text-sm font-semibold" style={{ color: C.text }}>{formatDate(booking.departure_date)}</p>
              <p className="text-xs mt-0.5" style={{ color: C.muted }}>{formatTimeOnly(booking.departure_time)}</p>
            </div>
          </div>

          {/* Guests */}
          <div>
            <p className="text-xs uppercase tracking-wide mb-1" style={{ color: C.muted }}>人数</p>
            <p className="text-sm" style={{ color: C.text }}>
              大人 {booking.adults ?? 0}名{booking.children ? ` · 子供 ${booking.children}名` : ''}{booking.infants ? ` · 幼児 ${booking.infants}名` : ''}
            </p>
          </div>

          {/* Channel / source */}
          <div>
            <p className="text-xs uppercase tracking-wide mb-1" style={{ color: C.muted }}>予約経路</p>
            <p className="text-sm" style={{ color: booking.source_override ? C.accent : C.text }}>
              {channelDisplay}
              {booking.source_override && booking.booking_channel && booking.source_override !== booking.booking_channel && (
                <span className="text-xs ml-1.5 line-through" style={{ color: C.muted }}>{booking.booking_channel}</span>
              )}
            </p>
            {booking.source_override && (
              <p className="text-[10px] mt-0.5" style={{ color: C.muted }}>手動で訂正済み</p>
            )}
          </div>

          {/* ── 現場ステータス ── */}
          <div style={{ borderTop: `1px solid ${C.border}` }} className="pt-4">
            <p className="text-xs uppercase tracking-wide mb-2" style={{ color: C.muted }}>現場ステータス</p>

            {/* Current status */}
            <div className="flex flex-wrap gap-3 mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>operational status</p>
                {isNoShow ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#A33A32', color: '#FFFEFA' }}>
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-forbid-line text-[10px]"></i></span>
                    ノーショー
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: booking.operational_status === 'checked_in' ? C.primary : booking.operational_status === 'checked_out' ? '#E5E3DD' : '#F0EDE5', color: booking.operational_status === 'checked_in' ? '#FFFEFA' : C.text }}>
                    {booking.operational_status || '—'}
                  </span>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>booking status</p>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: booking.booking_status === 'confirmed' ? C.primary : '#F0EDE5', color: booking.booking_status === 'confirmed' ? '#FFFEFA' : C.text }}>
                  {booking.booking_status || '—'}
                </span>
              </div>
            </div>

            {/* Planned & actual times */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>到着予定日時</p>
                  <p className="text-sm" style={{ color: C.text }}>
                    {formatDate(booking.arrival_date)} {formatTimeOnly(booking.arrival_time)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>出発予定日時</p>
                  <p className="text-sm" style={{ color: C.text }}>
                    {formatDate(booking.departure_date)} {formatTimeOnly(booking.departure_time)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>実チェックイン日時</p>
                  <p className="text-sm" style={{ color: booking.actual_check_in_at ? C.primary : C.muted }}>
                    {formatTimestamptzJST(booking.actual_check_in_at)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>実チェックアウト日時</p>
                  <p className="text-sm" style={{ color: booking.actual_check_out_at ? C.primary : C.muted }}>
                    {formatTimestamptzJST(booking.actual_check_out_at)}
                  </p>
                </div>
              </div>
              {!operatorsLoading && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>チェックイン操作者</p>
                    <p className="text-sm" style={{ color: operatorNames.checkedInBy ? C.text : C.muted }}>
                      {operatorNames.checkedInBy || '未記録'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>チェックアウト操作者</p>
                    <p className="text-sm" style={{ color: operatorNames.checkedOutBy ? C.text : C.muted }}>
                      {operatorNames.checkedOutBy || '未記録'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-3 pt-3 flex-wrap" style={{ borderTop: `1px solid ${C.border}` }}>
              {canCheckIn && (
                <button
                  onClick={() => setConfirmAction('check_in')}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: C.primary, color: '#FFFEFA' }}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-login-box-line text-xs"></i></span>
                  チェックインを記録
                </button>
              )}
              {canNoShow && (
                <button
                  onClick={() => setNoShowModalOpen(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'transparent', color: '#A33A32', border: '1px solid #A33A32' }}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-forbid-line text-xs"></i></span>
                  ノーショーを記録
                </button>
              )}
              {bookingEligible && checkinBlockedReason && (
                <span className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap" style={{ backgroundColor: '#F6F4EE', color: C.muted }}>
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line text-xs"></i></span>
                  {checkinBlockedReason}
                </span>
              )}
              {canCheckOut && (
                <button
                  onClick={() => setConfirmAction('check_out')}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: C.accent, color: '#FFFEFA' }}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-logout-box-line text-xs"></i></span>
                  チェックアウトを記録
                </button>
              )}
              {canImportedCheckOut && (
                <button
                  onClick={() => setConfirmAction('imported_check_out')}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: C.accent, color: '#FFFEFA' }}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-logout-box-line text-xs"></i></span>
                  チェックアウトを記録
                </button>
              )}
              {actionLoading && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line text-sm animate-spin" style={{ color: C.primary }}></i>
                </span>
              )}
            </div>
          </div>

          {/* Statuses */}
          <div className="flex flex-wrap gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: C.muted }}>Beds24 status</p>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#F0EDE5', color: C.text }}>
                {booking.beds24_status || '—'}
              </span>
            </div>
          </div>

          {/* Guest Operations */}
          {hasGuestOps && (
            <div>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: C.muted }}>ゲストオペレーション</p>
              <div className="space-y-1.5">
                {guestOps!.early_checkin_approved && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ backgroundColor: '#234638', color: '#FFFEFA' }}>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-double-line"></i></span>
                    Early check-in 承認済み · {formatTimestamptzJST(guestOps!.early_checkin_approved)}
                  </div>
                )}
                {guestOps!.early_checkin_requested && !guestOps!.early_checkin_approved && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ backgroundColor: '#E8E0C8', color: C.primary }}>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line"></i></span>
                    Early check-in 希望 · {formatTimestamptzJST(guestOps!.early_checkin_requested)}
                  </div>
                )}
                {guestOps!.late_checkout_approved && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ backgroundColor: '#234638', color: '#FFFEFA' }}>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-double-line"></i></span>
                    Late checkout 承認済み · {formatTimestamptzJST(guestOps!.late_checkout_approved)}
                  </div>
                )}
                {guestOps!.late_checkout_requested && !guestOps!.late_checkout_approved && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md text-sm" style={{ backgroundColor: '#E8E0C8', color: C.primary }}>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line"></i></span>
                    Late checkout 希望 · {formatTimestamptzJST(guestOps!.late_checkout_requested)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Beds24 Booking ID */}
          {booking.beds24_booking_id && (
            <div>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: C.muted }}>Beds24 Booking ID</p>
              <p className="text-xs font-mono" style={{ color: C.muted }}>{booking.beds24_booking_id}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Confirmation Dialog ── */}
      {confirmAction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="rounded-xl max-w-sm w-full p-6 space-y-4" style={{ backgroundColor: C.bg, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F0EDE5' }}>
                <i className="ri-question-line text-lg" style={{ color: C.accent }}></i>
              </span>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: C.text }}>
                  {confirmAction === 'check_in' ? 'チェックイン確認' : confirmAction === 'imported_check_out' ? 'チェックアウト確認' : 'チェックアウト確認'}
                </h3>
                <p className="text-sm mt-1" style={{ color: C.muted }}>
                  {confirmAction === 'imported_check_out'
                    ? `${roomName} / ${booking.guest_display_name || 'ゲスト'}様 — この予約はシステム導入前からの滞在です。チェックイン記録を補完してからチェックアウトを記録します。`
                    : `${roomName} / ${booking.guest_display_name || 'ゲスト'}様を${confirmAction === 'check_in' ? 'チェックイン済み' : 'チェックアウト済み'}にします。現在時刻と操作者が記録されます。`
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                style={{ backgroundColor: '#F0EDE5', color: C.text }}
              >
                キャンセル
              </button>
              <button
                onClick={confirmAction === 'check_in' ? handleConfirmCheckIn : confirmAction === 'imported_check_out' ? handleConfirmImportedCheckOut : handleConfirmCheckOut}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: confirmAction === 'check_in' ? C.primary : C.accent, color: '#FFFEFA' }}
              >
                {actionLoading ? (
                  <span className="w-4 h-4 flex items-center justify-center inline-block">
                    <i className="ri-loader-4-line text-sm animate-spin"></i>
                  </span>
                ) : (
                  confirmAction === 'check_in' ? 'チェックイン' : 'チェックアウト'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NoShow Modal ── */}
      {noShowModalOpen && booking && (
        <NoShowModal
          bookingName={booking.guest_display_name || 'ゲスト'}
          roomName={roomName}
          onClose={() => setNoShowModalOpen(false)}
          onSubmit={handleNoShowSubmit}
        />
      )}
    </>
  );
}